<template>
  <div class="page page-shop content">
    <h1 class="shop__title">{{ t('apiManager.title') }}</h1>
    <h2>{{ t('apiManager.apiKeysTitle') }}</h2>
    <template v-if="apiKeys && apiKeys.length > 0">
      <ul class="shop">
        <li class="shop__item" v-for="item in apiKeys" :key="item.apiKey">
          <api-key
            :keyValue="item.apiKey"
            :showAsDisabled="false"
            :keyDesc="item.description"
          />
        </li>
      </ul>
    </template>
    <template v-else>
      <p>{{ t('apiManager.noKeysText') }}</p>
      <p>
        {{ t('apiManager.learnMorePrefix') }}
        <a
          href="https://support.efriends.at/hc/de/articles/13102958854941"
          target="_blank"
          >{{ t('apiManager.learnMoreLink') }}</a
        >
      </p>
    </template>
    <p>
      <button
        type="button"
        class="api-key__add button button--primary button--small"
        @click="toggleNewKey"
      >
        +</button
      >{{ t('apiManager.addKeyButton') }}
    </p>
    <div v-if="showNewKey">
      <p>{{ t('apiManager.createKeyText') }}</p>
      <form id="newKeyForm">
        <div class="api-key__desc">
          <input
            type="text"
            id="newKeyDescField"
            v-model="newKeyDesc"
            :placeholder="t('apiManager.descriptionPlaceholder')"
          />
        </div>
        <div class="api-key__newbox">
          <input
            type="text"
            id="newKeyField"
            v-model="newKey"
            :placeholder="t('apiManager.newKeyPlaceholder')"
          />
          <button
            type="button"
            @click="genKey"
            class="button button--link api-key__gen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 299.32 299.32"
              :aria-label="t('apiManager.generateKeyAriaLabel')"
              role="img"
            >
              <path
                fill="currentColor"
                d="M59.52,128.087c-11.895,0-21.572,9.678-21.572,21.573s9.678,21.573,21.572,21.573s21.572-9.678,21.572-21.573
                S71.414,128.087,59.52,128.087z M59.52,156.233c-3.624,0-6.572-2.949-6.572-6.573s2.948-6.573,6.572-6.573s6.572,2.949,6.572,6.573
                S63.144,156.233,59.52,156.233z"
              />
              <path
                fill="currentColor"
                d="M297.349,150.704l-19.257-24.072c-1.708-2.135-4.294-3.378-7.028-3.378H151.472c-10.746-30.638-40.05-51.476-73.591-51.476
                C34.938,71.778,0,106.716,0,149.66s34.938,77.882,77.881,77.882c28.516,0,53.866-14.475,67.487-38.145h21.145l18.085,18.731
                c1.79,1.855,4.297,2.841,6.865,2.74c2.575-0.112,4.979-1.323,6.602-3.326l11.169-13.782l14.784,14.534
                c3.48,3.422,9.056,3.445,12.564,0.053l19.604-18.95h14.878c2.734,0,5.32-1.243,7.028-3.378l19.257-24.071
                C299.978,158.662,299.978,153.991,297.349,150.704z M266.738,171.397h-14.191c-2.334,0-4.577,0.907-6.255,2.529l-15.912,15.381
                l-15.593-15.329c-1.811-1.778-4.28-2.706-6.817-2.568c-2.533,0.144-4.888,1.349-6.484,3.32l-11.003,13.578l-13.675-14.163
                c-1.696-1.756-4.033-2.749-6.475-2.749H139.94c-3.465,0-6.622,1.989-8.118,5.115c-9.751,20.374-30.42,33.03-53.941,33.03
                C44.862,209.542,18,182.679,18,149.66s26.862-59.882,59.881-59.882c27.695,0,51.613,18.362,58.167,44.653
                c0.999,4.009,4.601,6.823,8.732,6.823h121.957l12.058,15.072L266.738,171.397z"
              />
            </svg>
          </button>
        </div>
        <button type="button" class="button button--primary" @click="saveKey">
          {{ t('global.save') }}
        </button>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, ref } from 'vue'
  import ApiKey from '@/components/api-manager/ApiKey.vue'
  import { useApiManagerStore } from '@/stores/useApiManagerStore'
  import { useStrings } from '@/composables/useStrings.ts'

  export default defineComponent({
    components: { ApiKey },
    setup(props, { }) {
      const apiManagerStore = useApiManagerStore()
      const { t } = useStrings()

      const loading = ref(true)
      apiManagerStore.loadApiKeys().then(() => (loading.value = false))

      const apiKeys = computed(() => apiManagerStore.apiKeys)
      const showNewKey = ref(false)
      const newKey = ref('')
      const newKeyDesc = ref('')
      const toggleNewKey = () => (showNewKey.value = !showNewKey.value)
      const genKey = function () {
        if (crypto.randomUUID) {
          newKey.value = crypto.randomUUID()
        } else {
          //for browsers without random UUID in crypto I found this https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
          let d = new Date().getTime(),
            d2 =
              (performance && performance.now && performance.now() * 1000) || 0
          newKey.value = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            (c) => {
              let r = Math.random() * 16
              if (d > 0) {
                r = (d + r) % 16 | 0
                d = Math.floor(d / 16)
              } else {
                r = (d2 + r) % 16 | 0
                d2 = Math.floor(d2 / 16)
              }
              return (c === 'x' ? r : (r & 0x7) | 0x8).toString(16)
            }
          )
        }
      }

      const saving = ref(false)
      const saveKey = () => {
        const newKeyField = document.getElementById('newKeyField') as HTMLInputElement
        const newKeyForm = document.getElementById('newKeyForm') as HTMLInputElement
        newKeyField.required = true
        newKeyField.minLength = 16

        if (!newKeyForm.reportValidity()) {
          return
        }

        saving.value = true
        apiManagerStore
          .saveApiKey({
            apiKey: newKey.value,
            apiKeyDesc: newKeyDesc.value,
          })
          .then(() => {
            showNewKey.value = false
            saving.value = false
            newKey.value = ''
            newKeyDesc.value = ''
          })
      }
      return {
        apiKeys,
        newKey,
        saveKey,
        genKey,
        toggleNewKey,
        showNewKey,
        loading,
        saving,
        newKeyDesc,
        t,
      }
    },
  })
</script>

<style lang="scss" scoped>
  #newKeyField {
    flex-grow: 1;
  }
  .api-key__newbox {
    display: flex;
    margin-bottom: 1em;
  }

  .api-key__add {
    font-size: $font-size-lg;
    font-weight: $font-weight-bold;
    margin: $spacer05 $spacer05 0 auto;
  }

  .api-key__gen {
    max-width: 3em;
    margin-left: 0.5rem;
  }

  .api-key__desc {
    margin-bottom: 1em;
  }

  .page-shop {
    max-width: 80ch;
  }

  .shop {
    display: grid;
    grid-template-columns: 1fr;
    gap: $spacer05;
    list-style: none;
    margin: 0 0 $spacer20 0;
    padding: 0;

    @media (min-width: $bp-md) {
      margin-bottom: $spacer40;
      gap: $spacer20;
    }
  }

  .shop__title {
    margin-bottom: 2rem;
  }

  .shop__message {
    margin-bottom: $spacer20;
  }
</style>
